import { Injectable } from '@angular/core';
import { FunctionalityRole } from '../models/FunctionalityRole';
import { PermissionService } from 'src/app/services/permission.service';
import { Alert } from '../models/Alert';
import { Observable, catchError, map, of, throwError } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ValidatePermissionsService{

  permissions: FunctionalityRole[] = [];

  dashboard: FunctionalityRole = <FunctionalityRole><any>{};
  home: FunctionalityRole = <FunctionalityRole><any>{};
  plans: FunctionalityRole = <FunctionalityRole><any>{};
  customers: FunctionalityRole = <FunctionalityRole><any>{};
  devices: FunctionalityRole = <FunctionalityRole><any>{};
  partners: FunctionalityRole = <FunctionalityRole><any>{};
  userManagement: FunctionalityRole = <FunctionalityRole><any>{};
  roleManagement: FunctionalityRole = <FunctionalityRole><any>{};
  notifications: FunctionalityRole = <FunctionalityRole><any>{};
  inventory: FunctionalityRole = <FunctionalityRole><any>{};
  sendSms: FunctionalityRole = <FunctionalityRole><any>{};
  tickets: FunctionalityRole = <FunctionalityRole><any>{};
  distributors: FunctionalityRole = <FunctionalityRole><any>{};

  nameRoleLogged: string = '';

  showDashboard: boolean = false;
  showHome: boolean = false;
  showPlans: boolean = false;
  showCustomers: boolean = false;
  showDevices: boolean = false;
  showPartners: boolean = false;
  showDistributors: boolean = false;
  showUserManagement: boolean = false;
  showRoleManagement: boolean = false;
  showNotifications: boolean = false;
  showInventory: boolean = false;
  showSendSms: boolean = false;
  showTickets: boolean = false;

  editPermission: boolean = false;
  archivePermission: boolean = false;
  createPermission: boolean = false;
  readPermission: boolean = false;
  get showPermission(): boolean { return this.editPermission || this.archivePermission || this.createPermission || this.readPermission }

  get isAdmin(): boolean { return this.roles.includes(USER_ROLE.ADMIN) }
  get isDistributor(): boolean { return this.roles.includes(USER_ROLE.DISTRIBUTOR) }
  get isPartner(): boolean { return this.roles.includes(USER_ROLE.PARTNER) }
  get isCustomer(): boolean { return this.roles.includes(USER_ROLE.CUSTOMER) }
  get isUserCustomer(): boolean { return this.roles.includes(USER_ROLE.USER_CUSTOMER) }
  get isStaff(): boolean { return this.roles.includes(USER_ROLE.STAFF) }

  /** Administrative roles*/
  //get isTechSupport(): boolean { return this.roles.includes('Tech_Support') }
  //get isBilling(): boolean { return this.roles.includes('Billing') }
  //get isCustomerSupport(): boolean { return this.roles.includes('Customer_Support') }

  /** Текущий пользователь относится к уровню Distributor: Distributor, DistributorStaff */
  get isDistributorLevel(): boolean { return this.isDistributor || this.isDistributorStaff }

  /** Текущий пользователь относится к уровню Partner: Partner, PartnerStaff */
  get isPartnerLevel(): boolean { return this.isPartner || this.isPartnerStaff }

  /** Текущий пользователь относится к уровню Customer: Customer, UserCustomer */
  get isCustomerLevel(): boolean { return this.isCustomer || this.isUserCustomer }

  /** Текущий пользователь является партнёром дистрибутора */
  get isDistributorPartner(): boolean { return this.isPartner && !!this.distributorId; }
  /** Текущий пользователь является партнёром который не привязан к дистрибутору */
  get isFreePartner(): boolean { return this.isPartner && !this.distributorId; }

  /** Текущий пользователь является персоналом дистрибутора */
  get isDistributorStaff(): boolean { return this.isStaff && !this.partnerId && !!this.distributorId; }
  /** Текущий пользователь является персоналом партнёра */
  get isPartnerStaff(): boolean { return this.isStaff && !!this.partnerId; }

  /** Текущий пользователь является кастомером дистрибутора */
  get isDistributorCustomer(): boolean { return this.isCustomer && !this.partnerId && !!this.distributorId; }
  /** Текущий пользователь является кастомером партнёра */
  get isPartnerCustomer(): boolean { return this.isCustomer && !!this.partnerId; }
  /** Текущий пользователь является кастомером который не привязан ни к дистрибутору ни к партнёру */
  get isFreeCustomer(): boolean { return this.isCustomer && !this.partnerId && !this.distributorId; }


  /// <summary>
  /// Идентификатор пользователя-владельца. 
  /// </summary>
  /// <remarks>
  /// Это может быть:
  ///     - дистрибутор, для самого дистрибутора или его пользователей штата;
  ///     - партнёр, для самого партнёра или его пользователей штата;
  ///     - клиент, для самого клиента или его пользователей.
  /// </remarks>
  get ownerId(): number {
    return (
      this.isDistributorLevel
        ? this.distributorId
        : this.isPartnerLevel
          ? this.partnerId
          : this.isCustomerLevel
            ? this.customerId
            : null
    ) ?? this.userId;
  }

  /// <summary>
  /// Название роли к которой относится пользователь-владелец.
  /// </summary>
  get ownerRole(): string  {
    return this.isDistributorLevel
      ? USER_ROLE.DISTRIBUTOR : this.isPartnerLevel
        ? USER_ROLE.PARTNER : this.isCustomerLevel
          ? USER_ROLE.CUSTOMER : this.role;
}


  roles: string[] = [];
  get role(): string { return this.roles.firstOrDefault() ?? '' }

  userId: number = 0;
  customerId?: number;
  partnerId?: number;
  distributorId?: number;


  accessLevels: string[] = [
    PERMISSION.Read,
    PERMISSION.Edit,
    PERMISSION.Archive,
    PERMISSION.Create,
  ];

  alert: Alert = {} as Alert;

  constructor(private permissionService: PermissionService) {
    this.loadPermissions$().subscribe();
  }

  /** Вызывается при создании сервиса и при переходах на страницы */
  public loadPermissions$() {
    return this.permissionService.getFuncionalities().pipe(
      map(({ Data }) => {
        this.permissions = Data;

        this.roles = this.permissions.map(permission => permission.nameRoleLogged);
        this.roles = [...new Set(this.roles)];

        this.userId = this.permissions.length > 0 ? this.permissions[0].userId : 0;
        this.customerId = this.permissions.length > 0 ? this.permissions[0].customerId : undefined;
        this.partnerId = this.permissions.length > 0 ? this.permissions[0].partnerId : undefined;
        this.distributorId = this.permissions.length > 0 ? this.permissions[0].distributorId : undefined;

        this.permissions.forEach(permission => this.updateSidebarVisibility(permission));
      }),
      catchError(error => {
        console.error('Error loading permissions:', error);
        return of(null);
      })
    );
  }

  public userPermission(idModule: number): FunctionalityRole {
    return this.permissions.firstOrDefault(permission => permission.idModule === idModule) ?? [];
  }

  public userHasPermission(idModule: number, requiredAccessLevels: string[]): boolean {
    return this.permissions.some(permission => permission.idModule === idModule && requiredAccessLevels.some((accessLevel) => permission[accessLevel]));
  }

  /*
  private userHasPermission$(idModule: number, requiredAccessLevels: string[]): Observable<boolean> {
    return this.permissionService.getFuncionalities().pipe(
      map(({ Data }) => {
        if (!Data) return false;

        this.roles = Data.map(permission => permission.nameRoleLogged);
        this.roles = [...new Set(this.roles)];

        this.userId = Data.length > 0 ? Data[0].userId : 0;
        this.customerId = Data.length > 0 ? Data[0].customerId : undefined;
        this.partnerId = Data.length > 0 ? Data[0].partnerId : undefined;
        this.distributorId = Data.length > 0 ? Data[0].distributorId : undefined;

        Data.forEach(permission => this.updateSidebarVisibility(permission));
        return Data.some(permission => permission.idModule === idModule && requiredAccessLevels.some((accessLevel) => permission[accessLevel]));
      }),
      catchError(error => throwError(() => error))
    );
  }
  */

  roleExists(role: string): boolean {
    return this.roles.includes(role);
  }

  inicializarPermisos() {
    this.showDashboard = true;
    this.showHome = true;
    this.showPlans = true;
    this.showCustomers = true;
    this.showDevices = true;
    this.showPartners = true;
    this.showDistributors = true;
    this.showUserManagement = true;
    this.showRoleManagement = true;
  }


  private initializeAccessLevels(permission?: FunctionalityRole): void {
    this.editPermission = !!permission?.editPermission;
    this.archivePermission = !!permission?.archivePermission;
    this.createPermission = !!permission?.createPermission;
    this.readPermission = !!permission?.readPermission;
  }

  private validatePermissionsAccessLevel(idModule: number, permission: FunctionalityRole): void {
    if (permission.idModule === idModule) {
      this.nameRoleLogged = permission.nameRoleLogged;
      this.accessLevels.forEach(accessLevel => {
        if (permission[accessLevel]) {
          switch (accessLevel) {
            case PERMISSION.Read: this.readPermission = true; break;
            case PERMISSION.Edit: this.editPermission = true; break;
            case PERMISSION.Archive: this.archivePermission = true; break;
            case PERMISSION.Create: this.createPermission = true; break;
          }
        }
      });
    }
  }

  private hasAnyPermission(readPermission: boolean, editPermission: boolean, archivePermission: boolean, createPermission: boolean): boolean {
    return (readPermission || editPermission || archivePermission || createPermission);
  }

  private updateSidebarVisibility(permission: FunctionalityRole): void {
    const idModule = permission.idModule;
    const readPermission = permission.readPermission;
    const editPermission = permission.editPermission;
    const archivePermission = permission.archivePermission;
    const createPermission = permission.createPermission;
    permission.anyPermission = this.hasAnyPermission(readPermission, editPermission, archivePermission, createPermission);

    switch (idModule) {
      case MODULE_ID.Dashboard: this.dashboard = permission; this.showDashboard = permission.anyPermission; break;
      case MODULE_ID.Home: this.home = permission; this.showHome = permission.anyPermission; break;
      case MODULE_ID.Plans: this.plans = permission; this.showPlans = permission.anyPermission; break;
      case MODULE_ID.Customers: this.customers = permission; this.showCustomers = permission.anyPermission; break;
      case MODULE_ID.Devices: this.devices = permission; this.showDevices = permission.anyPermission; break;
      case MODULE_ID.Partners: this.partners = permission; this.showPartners = permission.anyPermission; break;
      case MODULE_ID.UserManagement: this.userManagement = permission; this.showUserManagement = permission.anyPermission; break;
      case MODULE_ID.RoleManagement: this.roleManagement = permission; this.showRoleManagement = permission.anyPermission; break;
      case MODULE_ID.Notifications: this.notifications = permission; this.showNotifications = permission.anyPermission; break;
      case MODULE_ID.Inventory: this.inventory = permission; this.showInventory = permission.anyPermission; break;
      case MODULE_ID.SendSMS: this.sendSms = permission; this.showSendSms = permission.anyPermission; break;
      case MODULE_ID.Tickets: this.tickets = permission; this.showTickets = permission.anyPermission; break;
      case MODULE_ID.Distributors: this.distributors = permission; this.showDistributors = permission.anyPermission; break;
    }
  }

  public validateAccessLevels(idModule: number): void {
    this.initializeAccessLevels(this.userPermission(idModule));
  }
}
